import { useSelector } from 'react-redux';
import { generatePath } from '@bbkShared/react-router';
import {
  selectAppId,
  selectCompanyInternalName,
} from '@bbkAdminRedux/app/selectors';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import { useSelectControlFunnel } from '@bbkAdminRedux/rtkq/funnels.slice';
import { useSelectCurrentApp } from '@bbkAdminRedux/rtkq/current-user.slice';
import { usePlans } from '@bbkAdminUtils/plans';
import { parseSearchParams } from '@bbkAdminUtils/useSearchParams';
import { encodeObjectForQuery } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';

export const chainReturnUrlInSearchParams = (
  initSearchParams: { returnUrl?: string },
  prevReturnUrl?: string
) => {
  let returnUrlParams: { returnUrl?: string } = {};
  if (prevReturnUrl) {
    if (!initSearchParams.returnUrl) {
      returnUrlParams = {
        returnUrl: prevReturnUrl,
      };
    } else {
      const returnUrlObj = new URL(initSearchParams.returnUrl, 'http://mock');
      const returnUrlSearchParams = parseSearchParams(returnUrlObj.search);
      returnUrlParams = {
        returnUrl:
          returnUrlObj.pathname +
          encodeObjectForQuery({
            ...returnUrlSearchParams,
            returnUrl: prevReturnUrl,
          }) +
          returnUrlObj.hash,
      };
    }
  }
  return {
    ...initSearchParams,
    ...returnUrlParams,
  };
};

export const chainReturnUrl = (url: string, prevReturnUrl?: string) => {
  const { pathname, search, hash } = new URL(url, 'http://mock');
  const initSearchParams = parseSearchParams<{ returnUrl: string }>(search);

  const updatedSearchParams = chainReturnUrlInSearchParams(
    initSearchParams,
    prevReturnUrl
  );
  return pathname + encodeObjectForQuery(updatedSearchParams) + hash;
};

export function useUrl() {
  const appId = useSelector(selectAppId);
  const companyId = useSelector(selectCompanyInternalName);
  const generateUrl = <
    T extends { [x: string]: string | number | boolean | undefined },
  >(
    href = '',
    params?: T,
    previousReturnUrl?: string
  ) => {
    // set to empty string to avoid creating an error or empty path
    // rely on rollbar to surface the error
    if (href === '') {
      GlobalLogger.error('Undefined URL attempting to be built.');
    }
    if (
      href.includes('://') ||
      href.startsWith('/internal') ||
      href.startsWith('/admin') ||
      href.startsWith('/company/') ||
      href.startsWith('/dev')
    ) {
      return href;
    }

    let parsedHref;
    if (params) {
      parsedHref = generatePath(href, params);
    }

    const builtUrl = `/company/${companyId}/app/${appId}${parsedHref ?? href}`;
    return builtUrl.replace('//', '/');
  };

  return generateUrl;
}

export const useCreateControlFunnelUrl = () => {
  const createUrl = useUrl();
  const app = useSelectCurrentApp();
  const controlFunnel = useSelectControlFunnel({
    appKey: app?.encoded_id ?? '',
  });

  return createUrl(AuthorizedPaths.experiencesFunnelsPage, {
    funnelId: controlFunnel?.id ?? 0,
  });
};

export const useCreatePagesListUrl = (showFunnel = false) => {
  const createUrl = useUrl();
  const { isStarterPremiumPlan, isStarterFreePlan } = usePlans();
  const isStarterPlan = isStarterPremiumPlan || isStarterFreePlan;
  const controlFunnelUrl = useCreateControlFunnelUrl();

  return isStarterPlan || showFunnel
    ? controlFunnelUrl
    : createUrl(AuthorizedPaths.experiencesPages);
};
